import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Alert,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import DescriptionIcon from '@mui/icons-material/Description'
import LightbulbIcon from '@mui/icons-material/Lightbulb'

interface KeywordSuggestionResult {
  analysisContent: string[]
  suggestedKeywords: string[]
}

interface KeywordSuggestionModalProps {
  open: boolean
  onClose: () => void
  onApplyKeywords: (keywords: string[]) => void
  templateContent: string
  drugGroupName?: string
  existingKeywords: string[]
  isLoading?: boolean
  suggestionResult?: KeywordSuggestionResult | null
}

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
}))

const SuggestionGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: theme.spacing(1),
  alignItems: 'start',
}))

const KeywordSuggestionModal: React.FC<KeywordSuggestionModalProps> = ({
  open,
  onClose,
  onApplyKeywords,
  existingKeywords,
  isLoading = false,
  suggestionResult,
}) => {
  const [selectedKeywords, setSelectedKeywords] = useState<Set<string>>(
    new Set()
  )

  const handleKeywordToggle = (keyword: string) => {
    const newSelected = new Set(selectedKeywords)
    if (newSelected.has(keyword)) {
      newSelected.delete(keyword)
    } else {
      newSelected.add(keyword)
    }
    setSelectedKeywords(newSelected)
  }

  const handleApply = () => {
    const keywordsToAdd = Array.from(selectedKeywords).filter(
      (keyword) => !existingKeywords.includes(keyword)
    )
    onApplyKeywords(keywordsToAdd)
    setSelectedKeywords(new Set())
    onClose()
  }

  const handleClose = () => {
    setSelectedKeywords(new Set())
    onClose()
  }

  const duplicateKeywords = Array.from(selectedKeywords).filter((keyword) =>
    existingKeywords.includes(keyword)
  )

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <StyledDialogTitle>
        <AutoAwesomeIcon />
        キーワード提案
      </StyledDialogTitle>
      <DialogContent>
        <Box sx={{ height: 32 }} />
        {isLoading ? (
          <Box sx={{ py: 3 }}>
            <LinearProgress sx={{ mb: 2 }} />
            <Typography
              variant="body2"
              textAlign="center"
              color="text.secondary"
            >
              AIがテンプレート内容を分析しています...
            </Typography>
          </Box>
        ) : suggestionResult ? (
          <>
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
              >
                <DescriptionIcon fontSize="small" />
                分析内容
              </Typography>
              {suggestionResult.analysisContent.map((content, index) => (
                <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                  • {content}
                </Typography>
              ))}
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
              >
                <LightbulbIcon fontSize="small" />
                提案キーワード
              </Typography>

              <SuggestionGrid>
                {suggestionResult.suggestedKeywords.map((keyword, index) => {
                  const isExisting = existingKeywords.includes(keyword)
                  const isSelected = selectedKeywords.has(keyword)

                  return (
                    <FormControlLabel
                      key={index}
                      control={
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleKeywordToggle(keyword)}
                          disabled={isExisting}
                          sx={{ alignSelf: 'flex-start', pt: 0 }}
                        />
                      }
                      label={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="body2">
                            {keyword}
                            {isExisting && (
                              <Chip
                                label="追加済み"
                                size="small"
                                color="default"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Typography>
                        </Box>
                      }
                      sx={[
                        {
                          m: 0,
                          alignItems: 'flex-start',
                        },
                        isExisting
                          ? {
                              opacity: 0.6,
                            }
                          : {
                              opacity: 1,
                            },
                      ]}
                    />
                  )
                })}
              </SuggestionGrid>
            </Box>

            {duplicateKeywords.length > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                選択中のキーワードの一部は既に追加されているため、スキップされます:{' '}
                {duplicateKeywords.join(', ')}
              </Alert>
            )}
          </>
        ) : (
          <Alert severity="error">
            キーワードの提案に失敗しました。もう一度お試しください。
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          キャンセル
        </Button>
        {suggestionResult && !isLoading && (
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={selectedKeywords.size === 0}
            startIcon={<AutoAwesomeIcon />}
          >
            選択したキーワードを追加 ({selectedKeywords.size})
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default KeywordSuggestionModal
